import React, { useEffect } from "react";
import {
	BrowserRouter as Router,
	Switch,
	Route,
	Redirect,
	useLocation,
} from "react-router-dom";
import { ThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { theme } from "@utils/theme";
import { routes } from "@utils/constant";
import {
	headerTop,
	closeMobileMenu,
	getPermissions,
	isRolePermission
} from "@utils/commonFunctions";

import PageNotFoundView from "@views/404";
import JobDetailView from "@views/job-detail";
import LoginView from "@views/login";
import JobListing from "@views/job-listing";
import DriverListing from "@views/driver-listing";
import DriverDetail from "@views/driver-detail";
import CustomerDetail from "@views/customer-detail";
import CustomerListing from "@views/customer-listing";
import Dashboard from "@views/dashboard";
import ForgotPassword from "@views/forgot-password";
import ResetPassword from "@views/reset-password";
import ProfileView from "@views/settings/account";
import NotificationView from "@views/settings/notifications";
import TrucksView from "@views/settings/trucks";
import TollLocation from "@views/settings/toll-location";
import HolidaysView from "@views/settings/holidays";
import PrivateRoute from "@utils/privateRoute";
import TrailerView from "@views/settings/trailer";
import MapCockpitView from "@views/mapCockpit";
import OnlineRequestView from "@views/online-request";
import OnlineRequestDetail from "@views/online-request-detail";
import StaffUserView from "@views/settings/staff-user";
import ConnectedCustomerUsers from "@views/settings/connected-customer-users";
import RolesPermissionView from "@views/settings/roles-permission";
import Invoice from "@views/invoice";
import InvoiceDetail from "@views/invoice-detail";
import csvDetail from "@views/csv-detail";
import VerifyEmail from "@views/verify-email";
import ApprovedInvoice from "@views/approved-invoice";
import MIVInvoice from "@views/MIV-invoice";
import CreditListing from "@views/credit-listing";
import ApprovedCredit from "@views/approved-credit";
import Report from "@views/report";
import Organisation from "@views/settings/wymap-organisation";
import RejectedInvoice from "@views/rejected-invoice";
import ManagementDashboard from "@views/management-dashboard";
import CTOS from "@views/settings/cto";
import MyOrganisation from "@views/settings/my-organisation";
import OfflineJobListing from "@views/offline-jobs";
import OfflineJobDetail from "@views/offline-job-detail";
import JobPoolListing from "@views/job-pool-listing";
import FoundUIntegrationView from "@views/settings/foundU-integration";
import UnmatchedVehicles from "@views/settings/unmatched-vehicles";
import apiCredentialView from "@views/settings/api-credential";
import InterstateJobDetailView from "@views/interstate-job-detail";
import BackDatedEvents from "@views/back-dated-events";
import BackDatedEventsLog from "@views/back-dated-events-log";
import BackDatedEventDetail from "@views/back-dated-event-detail";
import MonthlyRateUpdates from "@views/monthly-rate-updates";

const usePaddingUpdater = () => {
	const location = useLocation();
	useEffect(() => {
		headerTop();
		closeMobileMenu();
		window.scrollTo(0, 0);
    document.body.classList.remove("open-filter");
	}, [location]);
};

const UpdateWrapper = () => {
	usePaddingUpdater();
	return (
		<Switch>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "dashboard")}
				exact
				path="/"
				component={Dashboard}
			/>
			<Route exact path={routes.login} component={LoginView} />
			<Route exact path={routes.forgotPassword} component={ForgotPassword} />
			<Route exact path={routes.resetPassword} component={ResetPassword} />
			<Route exact path={routes.verifyEmail} component={VerifyEmail} />
			<PrivateRoute
				authed={true}
				exact
				path={routes.credit}
				component={CreditListing}
			/>
			<PrivateRoute
				authed={true}
				exact
				path={routes.approvedCredit}
				component={ApprovedCredit}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "report")}
				exact
				path={routes.report}
				component={Report}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "trailer")}
				exact
				path={routes.trailer}
				component={TrailerView}
			/>
			<PrivateRoute
				exact
				authed={getPermissions() && isRolePermission(getPermissions(), "driver")}
				path={routes.driverListing}
				component={DriverListing}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "driver")}
				exact
				path={`${routes.driverDetail}/:id`}
				component={DriverDetail}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "invoice")}
				exact
				path={routes.invoice}
				component={Invoice}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "invoice")}
				exact
				path={`${routes.invoiceDetail}/:id`}
				component={InvoiceDetail}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "invoice")}
				exact
				path={`${routes.csvDetail}/:id`}
				component={csvDetail}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "invoice")}
				exact
				path={routes.approvedInvoice}
				component={ApprovedInvoice}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "invoice")}
				exact
				path={routes.rejectedInvoice}
				component={RejectedInvoice}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "invoice")}
				exact
				path={routes.mivInvoice}
				component={MIVInvoice}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "customer")}
				exact
				path={`${routes.customerDetail}/:id`}
				component={CustomerDetail}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "customer")}
				exact
				path={routes.customerListing}
				component={CustomerListing}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "customer")}
				exact
				path={`${routes.backDatedEvents}`}
				component={BackDatedEvents}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "customer")}
				exact
				path={`${routes.monthlyRateUpdates}`}
				component={MonthlyRateUpdates}
			/>

			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "customer")}
				exact
				path={`${routes.backDatedEventsLog}/:id`}
				component={BackDatedEventsLog}
			/>

			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "customer")}
				exact
				path={`${routes.backDatedEventDetail}/:id`}
				component={BackDatedEventDetail}
			/>
			<PrivateRoute
				authed={true}
				exact
				path={routes.myProfile}
				component={ProfileView}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "notification")}
				exact
				path={routes.notifications}
				component={NotificationView}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "truck")}
				exact
				path={routes.trucks}
				component={TrucksView}
			/>
      <PrivateRoute
        authed={getPermissions() && isRolePermission(getPermissions(), "unmatchedVehicles")}
        exact
        path={routes.unmatchedVehicles}
        component={UnmatchedVehicles}
      />
      <PrivateRoute
        authed={getPermissions() && isRolePermission(getPermissions(), "foundUIntegration")}
        exact
        path={routes.foundUIntegration}
        component={FoundUIntegrationView}
      />
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "tollLocation")}
				exact
				path={routes.tollLocation}
				component={TollLocation}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "dashboard")}
				exact
				path={routes.dashboard}
				component={Dashboard}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "dashboard")}
				exact
				path={routes.managementDashboard}
				component={ManagementDashboard}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "job")}
				exact
				path={routes.jobListing}
				component={JobListing}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "job")}
				exact
				path={routes.jobPoolListing}
				component={JobPoolListing}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "job")}
				exact
				path={`${routes.jobDetail}/:id`}
				component={JobDetailView}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "job")}
				exact
				path={routes.offlineJobListing}
				component={OfflineJobListing}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "job")}
				exact
				path={`${routes.offlineJobDetail}/:id`}
				component={OfflineJobDetail}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "mapCockpit")}
				exact
				path={routes.mapCockpit}
				component={MapCockpitView}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "onlineRequest")}
				exact
				path={routes.onlineRequest}
				component={OnlineRequestView}
			/>

			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "onlineRequest")}
				exact
				path={`${routes.onlineRequestDetail}/:id`}
				component={OnlineRequestDetail}
			/>

			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "holiday")}
				exact
				path={routes.holidays}
				component={HolidaysView}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "rolePermission")}
				exact
				path={routes.rolesPermission}
				component={RolesPermissionView}
			/>
			<PrivateRoute
				authed={true}
				exact
				path={routes.organisation}
				component={Organisation}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "user")}
				exact
				strict={true}
				path={routes.staffUsers}
				component={StaffUserView}
			/>
			<PrivateRoute
				authed={getPermissions() && isRolePermission(getPermissions(), "connectedCustomer")}
				exact
				strict={true}
				path={routes.connectedCustomerUsers}
				component={ConnectedCustomerUsers}
			/>
			<PrivateRoute
				authed={true}
				exact
				strict={true}
				path={routes.ctos}
				component={CTOS}
			/>
			<PrivateRoute
				authed={true}
				exact
				strict={true}
				path={routes.myOrganisation}
				component={MyOrganisation}
			/>
      <PrivateRoute
        authed={getPermissions() && isRolePermission(getPermissions(), "thirdPartyApiCredential")}
        exact
        strict={true}
        path={routes.apiCredential}
        component={apiCredentialView}
      />
      <PrivateRoute
        authed={getPermissions() && isRolePermission(getPermissions(), "job")}
        exact
        path={`${routes.interstateJobDetail}/:id`}
        component={InterstateJobDetailView}
      />

			<Route exact path={routes.pageNotFound} component={PageNotFoundView} />
			<Redirect to={routes.pageNotFound} />
		</Switch>
	);
};

const Routes = () => {
	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<Router>
				<UpdateWrapper />
			</Router>
		</ThemeProvider>
	);
};
export default Routes;
